import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  LogOut,
  User,
  Menu,
  X,
  Info,
  Users,
  Settings,
  BookOpen,
  MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import SaldoCreditos from '@/components/SaldoCreditos';
import TourBienvenida from '@/components/TourBienvenida';
import { BTN_VERDE } from '@/lib/estiloApp';

// Ayuda: la Guía de uso y el WhatsApp del titular, siempre a mano.
const WHATSAPP_AYUDA = `https://wa.me/5492216252996?text=${encodeURIComponent('Hola Fede, tengo una consulta sobre EVALPRO.')}`;

// tour: el paso del tour de bienvenida que señala este ítem.
// Panel unificado (01/11/2026): todo cuelga del alumno. Las entrevistas se ven
// en la ficha de cada uno; la lista completa sigue en /evaluations, con un
// link desde Alumnos, hasta que los grupos pasen al alumno (segunda etapa).
// activo: rutas que marcan el ítem como actual.
const navItems = [
  { path: '/alumnos', label: 'Alumnos', icon: User, tour: 'alumnos',
    activo: ['/alumnos', '/evaluation', '/evaluations'] },
  { path: '/groups', label: 'Grupos', icon: Users, activo: ['/groups'] },
  { path: '/configuracion', label: 'Configuración', icon: Settings, activo: ['/configuracion'] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const esActivo = (item) =>
    item.activo.some((r) => location.pathname === r || location.pathname.startsWith(`${r}/`));

  return (
    <div className="min-h-screen bg-[#f6f4ee]">
      {/* Barra del celular */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#0b0b0c] z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/logo-institucional.png" alt="Ejercicio Físico y Salud" className="h-12 w-auto object-contain" />
          <span className="font-['Anton'] text-[22px] tracking-[0.03em] text-[#efe6d2]">
            EVAL<span className="text-[#8CF0B4]">PRO</span>
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { setSidebarOpen(false); navigate('/new-evaluation'); }}
            className="h-9 w-9 rounded-full bg-[#27AE60] border-2 border-black text-white flex items-center justify-center"
            aria-label="Nuevo alumno"
            title="Nuevo alumno"
          >
            <Plus className="h-4 w-4" />
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#efe6d2] hover:bg-white/10 hover:text-white"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Menú lateral */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-64 bg-[#0b0b0c] z-40 transition-transform duration-300 flex flex-col overflow-y-auto",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="h-24 flex items-center gap-3 px-4 border-b border-white/10 shrink-0">
          <img src="/logo-institucional.png" alt="Ejercicio Físico y Salud" className="h-16 w-auto object-contain" />
          <div>
            <h1 className="font-['Anton'] text-[26px] leading-none tracking-[0.03em] text-[#efe6d2]">
              EVAL<span className="text-[#8CF0B4]">PRO</span>
            </h1>
            <p className="text-[11px] text-white/55 font-['Inter'] leading-tight mt-1">Ejercicio Físico y Salud</p>
          </div>
        </div>

        {/* Alta: arranca por la persona */}
        <div className="px-4 pt-6 pb-2">
          <button
            type="button"
            onClick={() => { setSidebarOpen(false); navigate('/new-evaluation'); }}
            className={cn(BTN_VERDE, 'w-full')}
            data-testid="nav-nuevo-alumno"
            data-tour="nueva"
          >
            <Plus className="h-4 w-4" />
            Nuevo alumno
          </button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = esActivo(item);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-[3px] font-['Special_Elite'] uppercase tracking-[0.09em] text-[13px] transition-colors",
                  isActive
                    ? "border-[#27AE60] bg-white/[0.07] text-[#efe6d2]"
                    : "border-transparent text-white/60 hover:bg-white/5 hover:text-[#efe6d2]"
                )}
                data-testid={`nav-${item.path.replace('/', '')}`}
                data-tour={item.tour}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Ayuda */}
        <div className="px-3 pb-3 space-y-1" data-tour="ayuda">
          <a
            href="/guia-uso-evalpro.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/5 hover:text-white font-['Inter']"
          >
            <BookOpen className="h-4 w-4" />
            Guía de uso
          </a>
          <a
            href={WHATSAPP_AYUDA}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/60 hover:bg-white/5 hover:text-white font-['Inter']"
          >
            <MessageCircle className="h-4 w-4" />
            Ayuda por WhatsApp
          </a>
        </div>

        {/* Saldo de entrevistas (solo cuentas con pack) */}
        <SaldoCreditos onNavigate={() => setSidebarOpen(false)} />

        {/* Aviso legal */}
        <div className="px-3 pb-4">
          <div className="p-3 bg-white/5 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-white/40 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-white/50 leading-relaxed font-['Inter']">
                Herramienta orientativa. No reemplaza valoración médica.
              </p>
            </div>
          </div>
        </div>

        <Separator className="bg-white/10" />

        {/* Usuario */}
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4 px-1">
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
              <User className="h-4 w-4 text-white/70" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate font-['Inter']">{user?.name}</p>
              <p className="text-xs text-white/50 truncate font-['Inter']">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start gap-2 border-white/20 bg-transparent text-white/70 hover:text-white hover:bg-white/10 font-['Inter']"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Fondo oscuro del menú en el celular */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-30 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <TourBienvenida />

      {/* Contenido */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8 max-w-6xl enter">
          {children}
        </div>
      </main>
    </div>
  );
}
